import { dqSortableAccordionSummaryClasses } from "@decentriq/components";
import {
  faAngleDown,
  faCircleExclamation,
  faInfoCircle,
  faXmark,
  type IconDefinition,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  accordionClasses,
  accordionSummaryClasses,
  alertClasses,
  type AlertOwnerState,
  type AlertProps,
  autocompleteClasses,
  Box,
  buttonClasses,
  dialogActionsClasses,
  dialogContentClasses,
  dialogTitleClasses,
  dividerClasses,
  iconButtonClasses,
  linkClasses,
  menuButtonClasses,
  modalDialogClasses,
  selectClasses,
  stepIndicatorClasses,
  stepperClasses,
  switchClasses,
  tabClasses,
  typographyClasses,
} from "@mui/joy";
import {
  type ColorPaletteProp,
  extendTheme,
  type Theme,
  type TypographySystem,
} from "@mui/joy/styles";
import { forwardRef } from "react";

/**
 * NOTE: Joy implements a color inversion engine, but it cannot fully predict the peculiarities of the design system,
 * thus in certain cases the inversion styles have to be manually adjusted — it does not know that for us the contrast
 * color of the solid variant is actually black for the "primary" and not white as it is for others, and it also does not know
 * that for us "success" equals "primary". The following patch bridges that gap in knowledge with the API that somewhat mimics
 * the official `applySolidInversion` utility, however at the moment this does not work with the `styled` or the `sx` prop
 * and is only meant for an internal use.
 */
const INVERTED_COLORS_ATTR = "data-skip-inverted-colors";
const INVERTED_COLORS_SELECTOR = `& :not([${INVERTED_COLORS_ATTR}], [${INVERTED_COLORS_ATTR}] *)`;
const applySolidInversionPatch =
  (color: ColorPaletteProp) => (theme: Theme) => ({
    [INVERTED_COLORS_SELECTOR]: {
      [`${theme.getColorSchemeSelector("light")}, ${theme.getColorSchemeSelector("dark")}`]:
        {
          "--variant-plainActiveColor": "var(--joy-palette-common-white)",
          ...((color === "primary" || color === "success") && {
            "--variant-plainActiveColor": "var(--joy-palette-common-black)",
            "--variant-plainColor": "var(--joy-palette-common-black)",
            "--variant-plainHoverColor": "var(--joy-palette-common-black)",
          }),
        },
    },
  });

const xmark = <FontAwesomeIcon icon={faXmark} />;
const indicator = <FontAwesomeIcon fixedWidth={true} icon={faAngleDown} />;
const indicatorActiveStyles = { transform: "rotate(-180deg)" };

// Fill in other color options if needed
const ALERT_ICON_MAP: Partial<
  Record<NonNullable<AlertProps["color"]>, IconDefinition>
> = {
  danger: faCircleExclamation,
};

export const joyTheme = extendTheme({
  colorSchemes: {
    light: {
      palette: {
        affinityRatio: {
          "0.5": "#EEF6F6",
          "0.6": "#EAF4F4",
          "0.7": "#E7F3F2",
          "0.8": "#E3F1F0",
          "0.9": "#E0F0EF",
          "1.0": "#DDEEED",
          "1.1": "#D9ECEB",
          "1.2": "#D6EBE9",
          "1.3": "#D3E9E7",
          "1.4": "#CFE7E5",
          "1.5": "#CCE6E3",
          "1.6": "#C8E4E1",
          "1.7": "#C4E2DF",
          "1.8": "#C1E1DD",
          "1.9": "#BEDFDB",
          "2.0": "#BADDD9",
        },
        chart: {
          100: "#BDF2EB",
          200: "#8BECE0",
          300: "#5BE5D5",
          400: "#2EDFCC",
          50: "#F2F9F8",
          500: "#03D8C3",
          600: "#18C2AF",
          700: "#29AB9B",
          800: "#349587",
          900: "#3A7E74",
        },
        chip: {
          alto: "#D7D7D7",
          celadon: "#BEE6B9",
          cinderella: "#FCD4CB",
          gin: "#F1F6F4",
          iceCold: "#A6F8CC",
          marzipan: "#F8E1A6",
          tropicalBlue: "#C1E7F7",
          wheat: "#F6D0B1",
        },
        danger: {
          100: "#F4C4C4",
          200: "#EC9B9B",
          300: "#E37575",
          400: "#DB5151",
          50: "#FCEFEF",
          500: "#D32F2F",
          600: "#C82828",
          700: "#BE2222",
          800: "#B31C1C",
          900: "#A81717",
          plainActiveColor: "var(--joy-palette-danger-900)",
          plainColor: "var(--joy-palette-danger-900)",
          plainHoverColor: "var(--joy-palette-danger-900)",
          softActiveBg: "var(--joy-palette-danger-200)",
          softActiveColor: "var(--joy-palette-danger-900)",
          softBg: "var(--joy-palette-danger-50)",
          softColor: "var(--joy-palette-danger-900)",
          softHoverBg: "var(--joy-palette-danger-100)",
          softHoverColor: "var(--joy-palette-danger-900)",
        },
        info: {
          100: "#E0F2FE",
          200: "#BAE6FD",
          300: "#7DD3FC",
          400: "#38BDF8",
          50: "#F0F9FF",
          500: "#0EA5E9",
          600: "#0284C7",
          700: "#0369A1",
          800: "#075985",
          900: "#0C4A6E",
          outlinedActiveBg: "var(--joy-palette-info-200)",
          outlinedBorder: "var(--joy-palette-info-300)",
          outlinedColor: "var(--joy-palette-info-500)",
          outlinedDisabledBorder: "var(--joy-palette-neutral-200)",
          outlinedDisabledColor: "var(--joy-palette-neutral-400)",
          outlinedHoverBg: "var(--joy-palette-info-100)",
          plainActiveBg: "var(--joy-palette-info-200)",
          plainActiveColor: "var(--joy-palette-info-900)",
          plainColor: "var(--joy-palette-info-900)",
          plainDisabledColor: "var(--joy-palette-neutral-400)",
          plainHoverBg: "var(--joy-palette-info-100)",
          plainHoverColor: "var(--joy-palette-info-900)",
          softActiveBg: "var(--joy-palette-info-200)",
          softActiveColor: "var(--joy-palette-info-900)",
          softBg: "var(--joy-palette-info-50)",
          softColor: "var(--joy-palette-info-900)",
          softDisabledBg: "var(--joy-palette-neutral-50)",
          softDisabledColor: "var(--joy-palette-neutral-400)",
          softHoverBg: "var(--joy-palette-info-100)",
          softHoverColor: "var(--joy-palette-info-900)",
          solidActiveBg: "var(--joy-palette-info-700)",
          solidBg: "var(--joy-palette-info-500)",
          solidColor: "var(--joy-palette-common-white)",
          solidDisabledBg: "var(--joy-palette-neutral-100)",
          solidDisabledColor: "var(--joy-palette-neutral-400)",
          solidHoverBg: "var(--joy-palette-info-600)",
        },
        primary: {
          100: "#BDF2EB",
          200: "#8BECE0",
          300: "#5BE5D5",
          400: "#2EDFCC",
          50: "#F2F9F8",
          500: "#03D8C3",
          600: "#18C2AF",
          700: "#29AB9B",
          800: "#349587",
          900: "#3A7E74",
          plainActiveColor: "var(--joy-palette-primary-900)",
          plainColor: "var(--joy-palette-primary-900)",
          plainHoverColor: "var(--joy-palette-primary-900)",
          softActiveBg: "var(--joy-palette-primary-200)",
          softActiveColor: "var(--joy-palette-primary-900)",
          softBg: "var(--joy-palette-primary-50)",
          softColor: "var(--joy-palette-primary-900)",
          softHoverBg: "var(--joy-palette-primary-100)",
          softHoverColor: "var(--joy-palette-primary-900)",
          solidColor: "var(--joy-palette-common-black)",
        },
        secondary: {
          100: "#E2D7F8",
          200: "#CDBBF3",
          300: "#B9A0EE",
          400: "#A586E9",
          50: "#F7F4FD",
          500: "#926DE4",
          600: "#8159DB",
          700: "#7146D1",
          800: "#6234C8",
          900: "#5424BE",
          outlinedActiveBg: "var(--joy-palette-secondary-200)",
          outlinedBorder: "var(--joy-palette-secondary-300)",
          outlinedColor: "var(--joy-palette-secondary-500)",
          outlinedDisabledBorder: "var(--joy-palette-neutral-200)",
          outlinedDisabledColor: "var(--joy-palette-neutral-400)",
          outlinedHoverBg: "var(--joy-palette-secondary-100)",
          plainActiveBg: "var(--joy-palette-secondary-200)",
          plainActiveColor: "var(--joy-palette-secondary-900)",
          plainColor: "var(--joy-palette-secondary-900)",
          plainDisabledColor: "var(--joy-palette-neutral-400)",
          plainHoverBg: "var(--joy-palette-secondary-100)",
          plainHoverColor: "var(--joy-palette-secondary-900)",
          softActiveBg: "var(--joy-palette-secondary-200)",
          softActiveColor: "var(--joy-palette-secondary-900)",
          softBg: "var(--joy-palette-secondary-50)",
          softColor: "var(--joy-palette-secondary-900)",
          softDisabledBg: "var(--joy-palette-neutral-50)",
          softDisabledColor: "var(--joy-palette-neutral-400)",
          softHoverBg: "var(--joy-palette-secondary-100)",
          softHoverColor: "var(--joy-palette-secondary-900)",
          solidActiveBg: "var(--joy-palette-secondary-700)",
          solidBg: "var(--joy-palette-secondary-500)",
          solidColor: "var(--joy-palette-common-white)",
          solidDisabledBg: "var(--joy-palette-neutral-100)",
          solidDisabledColor: "var(--joy-palette-neutral-400)",
          solidHoverBg: "var(--joy-palette-secondary-600)",
        },
        success: {
          100: "#BDF2EB",
          200: "#8BECE0",
          300: "#5BE5D5",
          400: "#2EDFCC",
          50: "#F2F9F8",
          500: "#03D8C3",
          600: "#18C2AF",
          700: "#29AB9B",
          800: "#349587",
          900: "#3A7E74",
          plainActiveColor: "var(--joy-palette-success-900)",
          plainColor: "var(--joy-palette-success-900)",
          plainHoverColor: "var(--joy-palette-success-900)",
          softActiveBg: "var(--joy-palette-success-200)",
          softActiveColor: "var(--joy-palette-success-900)",
          softBg: "var(--joy-palette-success-50)",
          softColor: "var(--joy-palette-success-900)",
          softHoverBg: "var(--joy-palette-success-100)",
          softHoverColor: "var(--joy-palette-success-900)",
          solidColor: "var(--joy-palette-common-black)",
        },
        warning: {
          100: "#FBD6BA",
          200: "#F8B98A",
          300: "#F59F5B",
          400: "#F2852D",
          50: "#FEF3EB",
          500: "#EF6C00",
          600: "#E36600",
          700: "#D66000",
          800: "#CA5B00",
          900: "#BD5500",
          plainActiveColor: "var(--joy-palette-warning-900)",
          plainColor: "var(--joy-palette-warning-900)",
          plainHoverColor: "var(--joy-palette-warning-900)",
          softActiveBg: "var(--joy-palette-warning-200)",
          softActiveColor: "var(--joy-palette-warning-900)",
          softBg: "var(--joy-palette-warning-50)",
          softColor: "var(--joy-palette-warning-900)",
          softHoverBg: "var(--joy-palette-warning-100)",
          softHoverColor: "var(--joy-palette-warning-900)",
        },
      },
    },
  },
  components: {
    DqSidePanel: {
      defaultProps: {
        anchor: "right",
        inset: true,
        variant: "outlined",
      },
    },
    DqSidePanelActions: {
      styleOverrides: {
        root: {
          [`&:has(.${buttonClasses.root}:only-child)`]: {
            [`& > .${buttonClasses.root}:only-child`]: { flex: "0 0 auto" },
            justifyContent: "flex-end",
          },
        },
      },
    },
    DqSortableAccordion: {
      defaultProps: {
        variant: "outlined",
      },
      styleOverrides: {
        root: {
          border:
            "var(--variant-borderWidth) solid var(--variant-outlinedBorder, var(--joy-palette-neutral-outlinedBorder, var(--joy-palette-neutral-300, #CDD7E1)))",
          borderRadius: "var(--List-radius)",
        },
      },
    },
    DqSortableAccordionDetails: {
      styleOverrides: {
        root: {
          borderBottomLeftRadius: "var(--List-radius)",
          borderBottomRightRadius: "var(--List-radius)",
        },
      },
    },
    DqSortableAccordionGroup: {
      defaultProps: {
        variant: "plain",
      },
      styleOverrides: {
        root: {
          "--List-radius":
            "calc(var(--ListItem-paddingX) + var(--joy-radius-sm))", // NOTE: Uses the expected radius of toolbar buttons
          "--ListItem-paddingX": "0.25rem",
          gap: "0.5rem",
        },
      },
    },
    DqSortableAccordionSummary: {
      defaultProps: {
        slotProps: {
          button: {
            sx: {
              [`.${dqSortableAccordionSummaryClasses.grip}`]: {
                opacity: 1,
                transition: "all 0.2s",
              },
              [`&:not(.Mui-selected,[aria-selected="true"],[aria-pressed="true"]):not(:hover) .${dqSortableAccordionSummaryClasses.grip}`]:
                {
                  marginRight: "calc((0.25rem * 2 + 0.5rem + 0.5rem) * -1)",
                  opacity: 0,
                },
              [`&:not(.Mui-selected,[aria-selected="true"],[aria-pressed="true"]):not(:hover) .${accordionSummaryClasses.indicator}`]:
                {
                  marginLeft: "0.25rem",
                },
            },
          },
          grip: {
            variant: "plain",
          },
        },
      },
      styleOverrides: {
        grip: {
          minWidth: "auto",
        },
        root: {
          borderRadius: "var(--List-radius)",
          [`& > .${accordionSummaryClasses.button}`]: {
            borderRadius: "var(--List-radius)",
          },
          [`& > .${accordionSummaryClasses.button}.${accordionSummaryClasses.expanded}`]:
            {
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            },
        },
      },
    },
    JoyAccordionDetails: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--joy-palette-background-body)",
        },
      },
    },
    JoyAccordionGroup: {
      defaultProps: {
        variant: "plain",
      },
      styleOverrides: {
        root: {
          "&.separated": {
            [`& .${accordionSummaryClasses.root}`]: {
              borderRadius: "var(--List-radius)",
              minHeight: "46px",
              [`& > .${accordionSummaryClasses.button}`]: {
                borderRadius: "var(--List-radius)",
              },
              [`& > .${accordionSummaryClasses.button}.${accordionSummaryClasses.expanded}`]:
                {
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                },
            },
            [`& .${accordionClasses.root}`]: {
              border:
                "var(--variant-borderWidth) solid var(--variant-outlinedBorder, var(--joy-palette-neutral-outlinedBorder, var(--joy-palette-neutral-300, #CDD7E1)))",
              borderRadius: "var(--List-radius)",
            },
            "--List-radius":
              "calc(var(--ListItem-paddingX) + var(--joy-radius-sm))", // NOTE: Uses the expected radius of toolbar buttons
            "--ListItem-paddingX": "0.25rem",
            gap: "0.5rem",
          },
        },
      },
    },
    JoyAccordionSummary: {
      defaultProps: {
        indicator,
      },
      styleOverrides: {
        indicator: {
          [`&.${selectClasses.expanded}`]: {
            ...indicatorActiveStyles,
          },
          transition: "0.2s",
        },
      },
    },
    JoyAlert: {
      defaultProps: {
        color: "info",
        slots: {
          startDecorator: ({ ownerState, className }) => {
            const color = (ownerState as AlertOwnerState).color ?? "info";
            return (
              <span className={className as string}>
                <FontAwesomeIcon icon={ALERT_ICON_MAP[color] ?? faInfoCircle} />
              </span>
            );
          },
        },
        // Setting startDecorator default not nullable prop is required to have slots override working
        startDecorator: <FontAwesomeIcon icon={faInfoCircle} />,
        variant: "soft",
      },
      styleOverrides: {
        root: ({ theme }) => ({
          alignItems: "flex-start",
          /**
           * NOTE: In case when the alert's title's `level` is changed instead of `Alert`'s size (to keep the current
           * alert's paddings, for example, which change with `size`), this will try to adjust the size of the `startDecorator`
           * text (and of other inline elements) to match the title's text size. This doesn't automatically account for
           * the theme extensions yet, and is also helpless against ad-hoc `sx` customizations. It's the responsibility
           * of the developer to adjust `slotProps` for the `startDecorator` accordingly in the event of such edge cases.
           */
          ...(
            [
              "h1",
              "h2",
              "h3",
              "h4",
              "title-lg",
              "title-md",
              "title-sm",
              "body-lg",
              "body-md",
              "body-sm",
              "body-xs",
            ] as (keyof TypographySystem)[]
          ).reduce(
            (memo, level) => ({
              ...memo,
              [`:has(> .${typographyClasses[level]}, > div > .${typographyClasses[level]}) > .${alertClasses.startDecorator}`]:
                {
                  "& > svg": {
                    fontSize: "inherit",
                  },
                  fontSize: theme.typography[level].fontSize,
                  lineHeight: theme.typography[level].lineHeight,
                },
            }),
            {},
          ),
        }),
        startDecorator: ({ ownerState, theme }) => ({
          "&:has(> svg)": {
            /**
             * NOTE: In case it holds an icon (but not a circular progress or other elements) aligns the content
             * with the title's baseline. If title's size (and not `Alert`'s) was overriden then this also relies
             * on the helper above to guestimate the font size and line height so that the baselines are aligned.
             */
            "& > svg": {
              fontSize:
                ownerState.size === undefined || ownerState.size === "md"
                  ? theme.fontSize.md
                  : "inherit",
            },
            alignSelf: "baseline",
            display: "inline-block",
            lineHeight: "inherit",
          },
        }),
      },
    },
    JoyAutocomplete: {
      defaultProps: {
        clearIcon: xmark,
        noOptionsText: "No more options",
        popupIcon: indicator,
      },
      styleOverrides: {
        input: {
          minWidth: "50%", // NOTE: Up from `30px`
        },
        popupIndicator: {
          "& > svg": {
            transition: "0.2s",
          },
          [`&.${autocompleteClasses.popupIndicatorOpen}`]: {
            "& > svg": {
              ...indicatorActiveStyles,
            },
            transform: "none",
          },
        },
        root: {
          boxShadow: "none",
        },
      },
    },
    JoyAvatar: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.size === "sm" && {
            fontSize: `calc(var(--Avatar-size, 2rem) * 0.4375)`,
            height: `var(--Avatar-size, 2rem)`,
            width: `var(--Avatar-size, 2rem)`, // Default as 14px
          }),
          ...(ownerState.size === "md" && {
            fontSize: `calc(var(--Avatar-size, 2.25rem) * 0.4444)`,
            height: `var(--Avatar-size, 2.25rem)`,
            width: `var(--Avatar-size, 2.25rem)`, // Default as 16px
          }),
          ...(ownerState.size === "lg" && {
            fontSize: `calc(var(--Avatar-size, 2.5rem) * 0.45)`,
            height: `var(--Avatar-size, 2.5rem)`,
            width: `var(--Avatar-size, 2.5rem)`, // Default as 18px
          }),
        }),
      },
    },
    JoyButton: {
      defaultProps: {
        color: "neutral",
        variant: "plain",
      },
    },
    JoyButtonGroup: {
      styleOverrides: {
        root: {
          [`& > .${iconButtonClasses.root}:last-child[aria-haspopup="menu"] > svg`]:
            { transition: "0.2s" },
          [`& > .${iconButtonClasses.root}:last-child[aria-haspopup="menu"][aria-expanded="true"] > svg`]:
            { ...indicatorActiveStyles },
        },
      },
    },
    JoyCheckbox: {
      styleOverrides: {
        action: ({ theme, ownerState }) => ({
          ...(ownerState.overlay &&
            ownerState.checked && {
              border: "1px solid",
              borderColor:
                theme.variants.solid[ownerState.color!]?.backgroundColor,
              inset: -1,
            }),
        }),
      },
    },
    JoyChip: {
      defaultProps: {
        size: "md",
      },
    },
    JoyChipDelete: {
      defaultProps: {
        children: xmark,
      },
    },
    JoyCircularProgress: {
      defaultProps: {
        color: "neutral",
        thickness: 1,
      },
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState?.size === "xs" && {
            "--CircularProgress-size": "18px",
            "--_root-size": "var(--CircularProgress-size, 18px)",
          }),
          ...(ownerState?.size === "xxs" && {
            "--CircularProgress-size": "14px",
            "--_root-size": "var(--CircularProgress-size, 14px)",
          }),
        }),
      },
    },
    JoyDialogActions: {
      defaultProps: {
        orientation: "horizontal",
      },
      styleOverrides: {
        root: {
          [`& > .${buttonClasses.root}:only-child`]: { flex: "0 0 auto" },
          justifyContent: "space-between",
          zIndex: "unset", // NOTE: Fixes the `z-index` issue with the drawer being used inside the modal dialog
        },
      },
    },
    JoyDrawer: {
      styleOverrides: {
        content: {
          "& > form": {
            flex: 1,
            overflow: "hidden",
          },
          [`& .${modalDialogClasses.root}`]: {
            height: "100%",
            maxHeight: "100%",
            maxWidth: "100%",
            minWidth: "100%",
            position: "relative",
            width: "100%",
          },
        },
      },
    },
    JoyFormControl: {
      styleOverrides: {
        root: {
          "--unstable_RadioGroup-margin": 0, // NOTE: By default Joy sets it to 0.5rem for yet unknown reasons
        },
      },
    },
    JoyFormHelperText: {
      styleOverrides: {
        root: {
          "&:empty": {
            display: "none",
          },
        },
      },
    },
    JoyIconButton: {
      styleOverrides: {
        root: {
          // TODO: @volodymyr.kushnir to add "narrow" class to all IconButton components which are next to each other
          "&.narrow": {
            minHeight: "auto",
            minWidth: "auto",
            padding: "6px",
          },
        },
      },
    },
    JoyInput: {
      styleOverrides: {
        root: {
          boxShadow: "none",
        },
      },
    },
    JoyLink: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          color: `var(--joy-palette-${ownerState?.color}-900, var(--joy-palette-neutral-900))`,
          textDecorationColor: "currentColor",
        }),
      },
    },
    JoyMenuButton: {
      defaultProps: {
        endDecorator: <FontAwesomeIcon icon={faAngleDown} />,
      },
      styleOverrides: {
        // @ts-expect-error // NOTE: The prop exists, but the type definition is missing
        endDecorator: {
          color: "var(--Icon-color)",
          fontSize: "1.14285714286em",
          marginInlineEnd: "calc(var(--Button-paddingInline, 1rem) / -4)",
          transition: "0.2s",
        },
        root: {
          [`&[aria-expanded="true"] > .${menuButtonClasses.endDecorator}`]: {
            ...indicatorActiveStyles,
          },
        },
        startDecorator: {
          color: "var(--Icon-color)", // NOTE: Should probably be applied to `FontAwesomeIcon`'s `svg` element the same way as it is applied to the `SvgIcon` component
          fontSize: "var(--Icon-fontSize)", // NOTE: Should probably be applied to `FontAwesomeIcon`'s `svg` element the same way as it is applied to the `SvgIcon` component
        },
      },
    },
    JoyModalDialog: {
      defaultProps: {
        minWidth: 400,
        variant: "plain",
      },
      styleOverrides: {
        root: ({ theme }) => ({
          /* 
            NOTE: ModalDialog considers itself a Card, thus applies Card padding.
            That CSS variable can be used to adjust the paddings of the title, content, and actions.
            The content is expanded to fill the entire width using the same technique as `CardOverflow` does.
            This allows for better scrollbar positioning and avoids cutting off overflowing shadows.
          */
          [`& > .${dialogActionsClasses.root}`]: {
            paddingTop: "var(--Card-padding)",
          },
          [`& > .${dialogContentClasses.root}`]: {
            marginInline: "calc(var(--Card-padding) * -1)",
            padding: "var(--Card-padding)",
          },
          [`& > .${dialogTitleClasses.root}`]: {
            paddingBottom: "var(--Card-padding)",
          },
          [`& > .${dialogTitleClasses.root} + .${dialogContentClasses.root}`]: {
            paddingTop: 0,
          },
          [`& > .${dividerClasses.root} + .${dividerClasses.root}`]: {
            display: "none", // NOTE: Regardless of how many dividers are rendered, only the first one is displayed
          },
          [`& > .${alertClasses.root}`]: {
            borderRadius: 0, // NOTE: Since the `Alert` is stretched, the border radius should be removed
            marginInline: "var(--Divider-inset)", // NOTE: Stretching `Alert`s to the full width of the dialog
          },
          "&[role=alertdialog]": {
            [`& > .${dialogContentClasses.root}`]: {
              textAlign: "center",
              textWrap: "balance",
            },
            [`& > .${dialogTitleClasses.root}`]: {
              "& >:not(svg)": {
                color: theme.palette.text.primary, // NOTE: The same effect can be achieved by using the `style={{ color: "initial" }}` prop on the `span` tag
              },
              alignItems: "center",
              flexDirection: "column",
              textAlign: "center",
              textWrap: "balance",
            },
            "--ModalDialog-maxWidth": "450px",
            "--ModalDialog-minWidth": "300px",
          },
          "--Card-padding": "1rem",
          backgroundColor: "var(--joy-palette-background-body)",
          gap: 0,
        }),
      },
    },
    JoyRadio: {
      styleOverrides: {
        action: ({ theme, ownerState }) => ({
          ...(ownerState.overlay &&
            ownerState.checked && {
              border: "1px solid",
              borderColor:
                theme.variants.solid[ownerState.color!]?.backgroundColor,
              inset: -1,
            }),
        }),
        radio: ({ theme, ownerState }) => ({
          ...(ownerState.checked && {
            ...theme.variants.solid[ownerState.color!],
            "&:hover": {
              ...theme.variants.solidHover[ownerState.color!],
            },
          }),
        }),
        root: ({ ownerState }) => ({
          ...(ownerState.overlay && { flexShrink: 0 }),
        }),
      },
    },
    JoySelect: {
      defaultProps: {
        indicator,
        slotProps: {
          button: ({ value }) => ({
            ...((value === null ||
              value === undefined ||
              value === "" ||
              (Array.isArray(value) && value.length === 0)) && {
              sx: {
                opacity: "var(--Select-placeholderOpacity)", // NOTE: Fixing opacity for multi-selects
              },
            }),
          }),
          listbox: {
            onKeyDown: (event) => {
              /*
                NOTE:
                Not sure if only Escape should not be propagated by default,
                but currently it is the only key that causes problems
                (e.g. closing the dialog along with the select's menu).
              */
              if (event.key === "Escape") {
                event.stopPropagation();
              }
            },
            placement: "bottom-start",
          },
        },
      },
      styleOverrides: {
        indicator: {
          [`&.${selectClasses.expanded}`]: {
            ...indicatorActiveStyles,
          },
          transition: "0.2s",
        },
        root: ({ theme, ownerState }) => ({
          '&:active, &[aria-pressed="true"]':
            theme.variants[`${ownerState.variant!}Active`]?.[ownerState.color!],
          boxShadow: "none",
        }),
      },
    },
    JoySheet: {
      styleOverrides: {
        root: ({ theme, ownerState: { color = "neutral", variant } }) => ({
          ...(variant === "solid" && applySolidInversionPatch(color)(theme)),
        }),
      },
    },
    JoyStack: {
      defaultProps: {
        spacing: 1,
        useFlexGap: true,
      },
    },
    JoyStepper: {
      styleOverrides: {
        root: {
          [`&.${stepperClasses.sizeSm} .${stepIndicatorClasses.root}`]: {
            fontSize: 12,
          },
        },
      },
    },
    JoySwitch: {
      defaultProps: {
        variant: "outlined",
      },
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          ...(ownerState.variant === "outlined" &&
            ownerState.checked && {
              [`& .${switchClasses.track}.${switchClasses.checked}`]: {
                ...theme.variants.solid[ownerState.color ?? "primary"],
              },
              [`&:hover .${switchClasses.track}.${switchClasses.checked}`]: {
                ...theme.variants.solidHover[ownerState.color ?? "primary"],
              },
              [`& .${switchClasses.thumb}.${switchClasses.checked}`]: {
                backgroundColor: theme.variants.solid.primary.color,
                boxShadow: "none",
                color: theme.variants.solid.primary.color,
              },
            }),
        }),
      },
    },
    JoyTab: {
      defaultProps: {
        color: "neutral",
        indicatorInset: true,
      },
    },
    JoyTabList: {
      styleOverrides: {
        root: {
          [`& .${tabClasses.root}`]: {
            backgroundColor: "transparent",
            [`&.${tabClasses.selected}`]: {
              "--Tab-indicatorColor": "var(--joy-palette-primary-500)",
              backgroundColor: "transparent",
            },
            [`&:hover:not(.${tabClasses.selected})`]: {
              "--Tab-indicatorColor": "var(--joy-palette-primary-500)",
              "--Tab-indicatorThickness": "2px",
              backgroundColor: "transparent",
            },
          },
        },
      },
    },
    JoyTabs: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--joy-palette-background-body)",
        },
      },
    },
    JoyTextarea: {
      styleOverrides: {
        root: {
          boxShadow: "none",
        },
      },
    },
    JoyTooltip: {
      defaultProps: {
        enterNextDelay: 100,
        leaveDelay: 100,
        placement: "top",
        slotProps: {
          // @ts-expect-error // NOTE: Wrongfully expects `Popper`'s `open` prop to be present
          root: {
            component: forwardRef(({ children, ownerState, ...props }, ref) => (
              <div ref={ref} {...props}>
                <Box sx={{ maxHeight: 200, overflow: "auto" }}>{children}</Box>
              </div>
            )),
          },
        },
      },
      styleOverrides: {
        root: {
          maxWidth: 400,
          [`& .${linkClasses.root}`]: {
            color: "inherit",
            fontSize: "inherit",
            lineHeight: "inherit",
            textDecoration: "underline",
          },
          padding: "8px",
        },
      },
    },
  },
});
